import React, { useEffect, useState } from 'react';
import { Headphones, BookOpen, Flame } from 'lucide-react';
import { authFetch } from '../../utils/authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const greetingForHour = (h) => {
  if (h < 5) return 'Still up';
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  if (h < 22) return 'Good evening';
  return 'Tonight';
};

/**
 * Top-of-Discover personalized strip.
 *  - Greeting tied to local hour
 *  - 3 stat chips: minutes listened (30d), items finished, current streak
 * Renders nothing until data loads — the layout collapses gracefully so
 * we don't push the hero down with placeholder space on mobile.
 */
const GreetingStrip = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    authFetch(`${BACKEND_URL}/api/discover/personal`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => alive && setData(d))
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  if (!data?.name) return null;

  const greeting = greetingForHour(new Date().getHours());
  const firstName = String(data.name).split(/[\s@]/)[0];
  const minLabel = data.minutes_listened
    ? data.minutes_listened >= 60
      ? `${(data.minutes_listened / 60).toFixed(1)}h listened`
      : `${data.minutes_listened} min listened`
    : null;

  const chips = [
    minLabel && {
      icon: Headphones,
      label: minLabel,
      tint: 'text-indigo-300 bg-indigo-500/10 border-indigo-500/30',
    },
    data.items_completed > 0 && {
      icon: BookOpen,
      label: `${data.items_completed} finished`,
      tint: 'text-emerald-300 bg-emerald-500/10 border-emerald-500/30',
    },
    data.streak_current > 0 && {
      icon: Flame,
      label: `${data.streak_current} day streak`,
      tint: 'text-amber-300 bg-amber-500/10 border-amber-500/30',
    },
  ].filter(Boolean);

  return (
    <div className="px-4 sm:px-6 mb-4" data-testid="discover-greeting">
      <p className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">
        {greeting}
      </p>
      <h2 className="text-lg sm:text-2xl text-white font-bold tracking-tight mt-0.5">
        {firstName}
        <span className="text-slate-400 font-normal text-base sm:text-xl">
          {' '}— pick up where you left off
        </span>
      </h2>
      {chips.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {chips.map((c, i) => {
            const Icon = c.icon;
            return (
              <span
                key={i}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border ${c.tint}`}
              >
                <Icon className="w-3 h-3" />
                {c.label}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default GreetingStrip;
